import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import Layout from '../components/Layout';
import { ChevronDown, ChevronUp, Search, Activity, ShieldCheck, AlertTriangle } from 'lucide-react';
import { useDrivers } from '../hooks/useDrivers';
import { useFleetCompliance } from '../hooks/useFleetCompliance';
import type { ComplianceStatus } from '../lib/types';

const BADGE: Record<ComplianceStatus, { label: string; className: string }> = {
  ok:        { label: 'Godkänd',    className: 'bg-emerald-50 text-emerald-700 border border-emerald-200' },
  warning:   { label: 'Varning',    className: 'bg-amber-50 text-amber-700 border border-amber-200' },
  violation: { label: 'Regelbrott', className: 'bg-red-50 text-red-700 border border-red-200' },
};

function ComplianceBadge({ status }: { status: ComplianceStatus }) {
  const b = BADGE[status];
  return (
    <span className={`inline-flex items-center px-2.5 py-1 rounded-lg text-xs font-semibold ${b.className}`}>
      {b.label}
    </span>
  );
}

const Tachograph: React.FC = () => {
  const [sortField, setSortField] = useState<'full_name' | 'performance_score' | 'total_trips'>('full_name');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
  const [entriesPerPage, setEntriesPerPage] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');

  const { drivers, total, loading } = useDrivers({
    search: searchTerm,
    page: currentPage,
    perPage: entriesPerPage,
    sortField,
    sortDirection,
  });
  const { compliance, loading: compLoading } = useFleetCompliance();

  const totalPages = Math.max(1, Math.ceil(total / entriesPerPage));

  const handleSort = (field: typeof sortField) => {
    if (sortField === field) setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    else { setSortField(field); setSortDirection('asc'); }
    setCurrentPage(1);
  };

  const renderSortIcon = (field: string) => {
    if (sortField !== field) return (
      <span className="ml-1 text-slate-300 inline-flex flex-col">
        <ChevronUp className="h-3 w-3" /><ChevronDown className="h-3 w-3 -mt-1" />
      </span>
    );
    return (
      <span className="ml-1 text-electric-500">
        {sortDirection === 'asc' ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
      </span>
    );
  };

  // Fleet summary stats from compliance data
  const compValues = Object.values(compliance);
  const activeSessions = compValues.reduce((sum, c) => sum + (c.todaySessions > 0 ? c.todaySessions : 0), 0);
  const okCount = compValues.filter(c => c.status === 'ok').length;
  const violationCount = compValues.filter(c => c.status === 'violation').length;
  const totalWithData = compValues.length;

  return (
    <Layout
      title="Tachograph"
      breadcrumbs={[{ label: 'Home', path: '/dashboard' }, { label: 'Tachograph' }]}
    >
      <div className="space-y-5">

        {/* Fleet Compliance Summary */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div className="card p-5 flex items-center gap-4">
            <div className="w-11 h-11 rounded-2xl flex items-center justify-center shrink-0"
              style={{ background: 'rgba(6,182,212,0.12)', color: '#06B6D4' }}>
              <Activity className="w-5 h-5" />
            </div>
            <div>
              <div className="text-2xl font-bold text-slate-800" style={{ fontFamily: 'Space Grotesk, Inter, sans-serif' }}>
                {compLoading ? '—' : activeSessions}
              </div>
              <div className="text-xs text-slate-500">Aktiva körpass idag</div>
            </div>
          </div>

          <div className="card p-5 flex items-center gap-4">
            <div className="w-11 h-11 rounded-2xl flex items-center justify-center shrink-0"
              style={{ background: 'rgba(16,185,129,0.12)', color: '#10b981' }}>
              <ShieldCheck className="w-5 h-5" />
            </div>
            <div>
              <div className="text-2xl font-bold text-slate-800" style={{ fontFamily: 'Space Grotesk, Inter, sans-serif' }}>
                {compLoading ? '—' : totalWithData > 0 ? `${okCount}/${totalWithData}` : 'Inga data'}
              </div>
              <div className="text-xs text-slate-500">Regelefterlevnad idag</div>
            </div>
          </div>

          <div className="card p-5 flex items-center gap-4">
            <div className="w-11 h-11 rounded-2xl flex items-center justify-center shrink-0"
              style={{ background: violationCount > 0 ? 'rgba(239,68,68,0.12)' : 'rgba(16,185,129,0.12)',
                       color: violationCount > 0 ? '#ef4444' : '#10b981' }}>
              <AlertTriangle className="w-5 h-5" />
            </div>
            <div>
              <div className={`text-2xl font-bold ${violationCount > 0 ? 'text-red-600' : 'text-emerald-600'}`}
                style={{ fontFamily: 'Space Grotesk, Inter, sans-serif' }}>
                {compLoading ? '—' : violationCount}
              </div>
              <div className="text-xs text-slate-500">Regelbrott idag</div>
            </div>
          </div>
        </div>

        {/* Driver Table */}
        <div className="card overflow-hidden">
          {/* Toolbar */}
          <div className="px-5 py-4 border-b border-slate-100 flex flex-wrap items-center justify-between gap-4">
            <div className="flex items-center gap-2 text-sm text-slate-600">
              <span>Visa</span>
              <select
                value={entriesPerPage}
                onChange={(e) => { setEntriesPerPage(Number(e.target.value)); setCurrentPage(1); }}
                className="border border-slate-200 rounded-lg px-2 py-1.5 text-sm bg-white focus:outline-none focus:border-electric-400"
              >
                <option value={10}>10</option>
                <option value={25}>25</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
              </select>
              <span>per sida</span>
            </div>

            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <input
                type="text"
                placeholder="Sök förare..."
                className="pl-9 pr-4 py-2 w-72 text-sm rounded-xl border border-slate-200 bg-slate-50 focus:outline-none focus:border-electric-400 focus:ring-2 focus:ring-electric-400/15 focus:bg-white transition-all"
                value={searchTerm}
                onChange={(e) => { setSearchTerm(e.target.value); setCurrentPage(1); }}
              />
            </div>
          </div>

          {/* Table */}
          <div className="overflow-x-auto">
            <table className="min-w-full table-premium">
              <thead>
                <tr>
                  {([
                    { label: 'Förare',       field: 'full_name' as const },
                    { label: 'Fordon',       field: null },
                    { label: 'Körpass idag', field: null },
                    { label: 'Körtid idag',  field: null },
                    { label: 'Regelefterlevnad', field: null },
                  ]).map(({ label, field }) => (
                    <th
                      key={label}
                      onClick={field ? () => handleSort(field) : undefined}
                      className={field ? 'cursor-pointer select-none' : ''}
                    >
                      <div className="flex items-center gap-1">
                        {label}
                        {field && renderSortIcon(field)}
                      </div>
                    </th>
                  ))}
                  <th>Detalj</th>
                </tr>
              </thead>
              <tbody>
                {loading
                  ? [...Array(entriesPerPage)].map((_, i) => (
                    <tr key={i}>
                      {[...Array(6)].map((__, j) => (
                        <td key={j}><div className="skeleton h-4 rounded w-full" /></td>
                      ))}
                    </tr>
                  ))
                  : drivers.map((driver) => {
                      const comp = compliance[driver.id];
                      const todayH = comp ? (comp.todayMinutes / 60).toFixed(1) + 'h' : '—';
                      const sessions = comp ? comp.todaySessions : '—';
                      const status: ComplianceStatus = comp ? comp.status : 'ok';

                      return (
                        <tr key={driver.id}>
                          <td className="font-medium text-slate-800">{driver.full_name}</td>
                          <td className="text-slate-500 text-sm">
                            {driver.vehicle ? `${driver.vehicle.plate} (${driver.vehicle.model})` : '—'}
                          </td>
                          <td>
                            <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg bg-slate-100 text-slate-700 text-xs font-medium">
                              {compLoading ? '…' : sessions}
                            </span>
                          </td>
                          <td className="text-sm font-semibold text-slate-700">
                            {compLoading ? '…' : todayH}
                          </td>
                          <td>
                            {compLoading
                              ? <div className="skeleton h-6 w-20 rounded" />
                              : comp
                                ? <ComplianceBadge status={status} />
                                : <span className="text-xs text-slate-400">Inga data</span>
                            }
                          </td>
                          <td>
                            <Link
                              to={`/tachograph/${driver.id}`}
                              className="inline-flex items-center gap-1 text-xs font-semibold text-electric-600 hover:text-electric-800 hover:bg-electric-50 px-3 py-1.5 rounded-lg transition"
                            >
                              Visa
                              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                              </svg>
                            </Link>
                          </td>
                        </tr>
                      );
                    })
                }
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <div className="px-5 py-4 flex flex-wrap items-center justify-between gap-3 border-t border-slate-100 bg-slate-50/60">
            <div className="text-sm text-slate-500">
              Visar{' '}
              <span className="font-medium text-slate-700">{total === 0 ? 0 : (currentPage - 1) * entriesPerPage + 1}</span>
              {' – '}
              <span className="font-medium text-slate-700">{Math.min(currentPage * entriesPerPage, total)}</span>
              {' av '}
              <span className="font-medium text-slate-700">{total}</span> förare
            </div>
            <div className="flex items-center gap-1.5">
              <button
                onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                disabled={currentPage === 1}
                className="px-3 py-1.5 text-sm border border-slate-200 rounded-lg bg-white text-slate-600 hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed transition"
              >
                ← Föregående
              </button>
              {[...Array(Math.min(5, totalPages))].map((_, i) => {
                const p = i + 1;
                return (
                  <button
                    key={i}
                    onClick={() => setCurrentPage(p)}
                    className={`w-8 h-8 text-sm rounded-lg font-medium transition ${currentPage === p
                      ? 'text-white shadow-glow-cyan-sm'
                      : 'border border-slate-200 bg-white text-slate-600 hover:bg-slate-50'
                    }`}
                    style={currentPage === p ? { background: 'linear-gradient(135deg,#06B6D4,#0891B2)' } : {}}
                  >
                    {p}
                  </button>
                );
              })}
              <button
                onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                disabled={currentPage === totalPages}
                className="px-3 py-1.5 text-sm border border-slate-200 rounded-lg bg-white text-slate-600 hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed transition"
              >
                Nästa →
              </button>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default Tachograph;
